import React, { useEffect, useMemo, useRef, useState } from "react";

import type { EmotionalIntentSegment, NarrativeSonicsContext } from "./NarrativeSonics.types";

type ArcLensProps = {
  context: NarrativeSonicsContext;
  segments: EmotionalIntentSegment[];
  onUpsertSegment: (segment: EmotionalIntentSegment) => void;
  onRemoveSegment: (segmentId: string) => void;
  compact?: boolean;
  embedded?: boolean;
};

type ArcDraft = {
  startSec: number;
  endSec: number;
  valence: number;
  intensity: number;
  urgency: number;
};

type DragMode = "start" | "end" | "range";

type ArcLayerTab = "ribbon" | "recipes" | "segments";

type ArcRecipe = {
  label: string;
  summary: string;
  tone: "calm" | "pressure" | "resolve";
  valence: number;
  intensity: number;
  urgency: number;
};

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function makeArcIntentId(): string {
  try {
    const c = globalThis.crypto as Crypto | undefined;
    if (c?.randomUUID) return c.randomUUID();
  } catch {
    // ignore
  }
  return `arc-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function fmt(sec: number | undefined): string {
  const safe = typeof sec === "number" && Number.isFinite(sec) ? Math.max(0, sec) : 0;
  const mm = Math.floor(safe / 60);
  const ss = Math.floor(safe % 60)
    .toString()
    .padStart(2, "0");
  return `${mm}:${ss}`;
}

function fmtSigned(value: number): string {
  const sign = value > 0 ? "+" : value < 0 ? "-" : "";
  return `${sign}${Math.abs(value).toFixed(2)}`;
}

function buildDefaultDraft(context: NarrativeSonicsContext): ArcDraft {
  const durationSec = Math.max(1, Number(context.durationSec ?? 12));
  const startSec = clamp(Number(context.currentTimeSec ?? 0), 0, Math.max(0, durationSec - 0.75));
  const endSec = clamp(startSec + Math.min(4, durationSec), startSec + 0.5, durationSec);

  return {
    startSec,
    endSec,
    valence: 0.15,
    intensity: 0.62,
    urgency: 0.48,
  };
}

function toDraft(segment: EmotionalIntentSegment): ArcDraft {
  return {
    startSec: segment.startSec,
    endSec: segment.endSec,
    valence: segment.valence,
    intensity: segment.intensity,
    urgency: segment.urgency,
  };
}

function focusLabel(context: NarrativeSonicsContext): string {
  return context.focusClipId || context.focusSourceId || context.focusSequenceId || context.focusScope;
}

function pct(sec: number, durationSec: number): number {
  if (!Number.isFinite(durationSec) || durationSec <= 0) return 0;
  return clamp((sec / durationSec) * 100, 0, 100);
}

function vectorMagnitude(draft: ArcDraft): number {
  return Math.sqrt((draft.valence * draft.valence) + (draft.intensity * draft.intensity) + (draft.urgency * draft.urgency)) / Math.sqrt(3);
}

function emotionalGradient(draft: ArcDraft): string {
  const warm = draft.urgency;
  const cool = 1 - draft.urgency;
  const lift = draft.valence >= 0 ? draft.valence : 0;
  const weight = draft.valence < 0 ? Math.abs(draft.valence) : 0;
  const intensity = clamp(draft.intensity, 0, 1);
  return `linear-gradient(90deg,
    rgba(${Math.round(96 + (40 * cool))}, ${Math.round(165 + (70 * cool))}, 250, ${0.28 + (0.18 * intensity)}) 0%,
    rgba(${Math.round(168 + (40 * warm))}, ${Math.round(85 + (60 * lift))}, ${Math.round(247 - (30 * weight))}, ${0.24 + (0.18 * intensity)}) 38%,
    rgba(${Math.round(251 - (18 * cool))}, ${Math.round(191 - (35 * cool))}, ${Math.round(36 + (24 * warm))}, ${0.26 + (0.2 * draft.urgency)}) 72%,
    rgba(${Math.round(34 + (18 * lift))}, ${Math.round(197 + (26 * lift))}, ${Math.round(94 + (40 * lift))}, ${0.24 + (0.18 * Math.max(lift, 0.18))}) 100%)`;
}

function recipeToneClasses(tone: ArcRecipe["tone"]): string {
  switch (tone) {
    case "calm":
      return "border-emerald-300/22 bg-emerald-400/12 text-emerald-50";
    case "pressure":
      return "border-amber-300/24 bg-amber-400/14 text-amber-50";
    case "resolve":
      return "border-sky-300/24 bg-sky-400/14 text-sky-50";
    default:
      return "border-white/10 bg-white/[0.06] text-white/82";
  }
}

function vectorToneClasses(key: "valence" | "intensity" | "urgency", value: number): string {
  if (key === "valence") {
    return value >= 0
      ? "border-emerald-300/22 bg-emerald-400/12 text-emerald-50"
      : "border-rose-300/22 bg-rose-400/12 text-rose-50";
  }
  if (key === "intensity") return "border-cyan-300/22 bg-cyan-400/12 text-cyan-50";
  return "border-amber-300/24 bg-amber-400/14 text-amber-50";
}

function SegmentChip({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <span className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.06] px-2.5 py-1 text-[10px] font-black tracking-[0.16em] text-white/72">
      {label} {value}
    </span>
  );
}

function VectorCard({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint: string;
  tone: string;
}): JSX.Element {
  return (
    <div className="rounded-[18px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.03))] p-3 transition duration-200 hover:border-white/16 hover:bg-white/[0.07]">
      <div className="flex items-start justify-between gap-2">
        <span className="text-[10px] font-black uppercase tracking-[0.18em] text-white/52">{label}</span>
        <span className={["inline-flex h-6 items-center rounded-full border px-2.5 text-[10px] font-black tracking-[0.16em]", tone].join(" ")}>{value}</span>
      </div>
      <div className="mt-2 text-[12px] leading-5 text-white/70">{hint}</div>
    </div>
  );
}

function ControlSlider({
  label,
  value,
  min,
  max,
  step,
  onChange,
  accent,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  accent: string;
}): JSX.Element {
  return (
    <label className="space-y-2 rounded-[18px] border border-white/10 bg-black/12 px-3 py-3 transition duration-200 hover:border-white/16 hover:bg-black/16">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-black uppercase tracking-[0.18em] text-white/56">{label}</span>
        <span className="text-[11px] font-semibold text-white/78">{value.toFixed(2)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        className={`w-full ${accent}`}
      />
    </label>
  );
}

function LayerTabButton({
  active,
  label,
  summary,
  onClick,
  compact = false,
}: {
  active: boolean;
  label: string;
  summary: string;
  onClick: () => void;
  compact?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={compact ? "rounded-[14px] border px-2.5 py-1.5 text-left transition duration-200 hover:-translate-y-[1px]" : "rounded-[18px] border px-3 py-2.5 text-left transition duration-200 hover:-translate-y-[1px]"}
      style={{
        borderColor: active ? "rgba(52,211,153,0.30)" : "rgba(255,255,255,0.10)",
        background: active
          ? "linear-gradient(180deg, rgba(16,185,129,0.14), rgba(6,78,59,0.24))"
          : "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.03))",
      }}
    >
      <div className={compact ? "text-[9px] font-black uppercase tracking-[0.16em] text-white/62" : "text-[10px] font-black uppercase tracking-[0.18em] text-white/58"}>{label}</div>
      {!compact ? <div className="mt-1 text-[12px] leading-5 text-white/74">{summary}</div> : null}
    </button>
  );
}

export default function ArcLens({
  context,
  segments,
  onUpsertSegment,
  onRemoveSegment,
  compact = false,
  embedded = false,
}: ArcLensProps): JSX.Element {
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(null);
  const [draft, setDraft] = useState<ArcDraft>(() => buildDefaultDraft(context));
  const [dragMode, setDragMode] = useState<DragMode | null>(null);
  const [activeTab, setActiveTab] = useState<ArcLayerTab>("ribbon");
  const trackRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<{
    mode: DragMode;
    pointerStartSec: number;
    startSec: number;
    endSec: number;
  } | null>(null);

  const selectedSegment = useMemo(
    () => segments.find((segment) => segment.id === selectedSegmentId) ?? null,
    [segments, selectedSegmentId],
  );

  useEffect(() => {
    if (selectedSegment) {
      setDraft(toDraft(selectedSegment));
      return;
    }

    setDraft(buildDefaultDraft(context));
  }, [context, selectedSegment]);

  useEffect(() => {
    if (!selectedSegmentId) return;
    const stillExists = segments.some((segment) => segment.id === selectedSegmentId);
    if (!stillExists) setSelectedSegmentId(null);
  }, [segments, selectedSegmentId]);

  const durationSec = Math.max(1, Number(context.durationSec ?? 12));
  const sortedSegments = useMemo(
    () => [...segments].sort((a, b) => a.startSec - b.startSec),
    [segments],
  );
  const recipes: ArcRecipe[] = [
    {
      label: "Calm Rise",
      summary: "Lift emotional tone without introducing pressure.",
      tone: "calm",
      valence: 0.42,
      intensity: 0.34,
      urgency: 0.24,
    },
    {
      label: "Pressure",
      summary: "Push intensity and urgency while slightly lowering comfort.",
      tone: "pressure",
      valence: -0.12,
      intensity: 0.78,
      urgency: 0.84,
    },
    {
      label: "Resolve",
      summary: "Land the beat with positive valence and controlled urgency.",
      tone: "resolve",
      valence: 0.58,
      intensity: 0.48,
      urgency: 0.30,
    },
  ];

  const currentRangeLabel = `${fmt(draft.startSec)} - ${fmt(draft.endSec)}`;
  const selectionLabel = focusLabel(context);
  const vectorLabel = `${fmtSigned(draft.valence)} · ${draft.intensity.toFixed(2)} · ${draft.urgency.toFixed(2)}`;
  const ribbonBackground = emotionalGradient(draft);
  const segmentCountLabel = `${sortedSegments.length} segment${sortedSegments.length === 1 ? "" : "s"}`;

  const secFromClientX = React.useCallback((clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0) return 0;
    const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
    return ratio * durationSec;
  }, [durationSec]);

  useEffect(() => {
    if (!dragMode) return;

    const handlePointerMove = (event: PointerEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState) return;

      const nextSec = secFromClientX(event.clientX);
      if (dragState.mode === "start") {
        setDraft((prev) => ({
          ...prev,
          startSec: clamp(nextSec, 0, prev.endSec - 0.1),
        }));
        return;
      }

      if (dragState.mode === "end") {
        setDraft((prev) => ({
          ...prev,
          endSec: clamp(nextSec, prev.startSec + 0.1, durationSec),
        }));
        return;
      }

      const delta = nextSec - dragState.pointerStartSec;
      const originalLength = dragState.endSec - dragState.startSec;
      const nextStartSec = clamp(dragState.startSec + delta, 0, Math.max(0, durationSec - originalLength));
      const nextEndSec = clamp(nextStartSec + originalLength, nextStartSec + 0.1, durationSec);
      setDraft((prev) => ({
        ...prev,
        startSec: nextStartSec,
        endSec: nextEndSec,
      }));
    };

    const handlePointerUp = () => {
      dragStateRef.current = null;
      setDragMode(null);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [dragMode, durationSec, secFromClientX]);

  const beginDrag = (mode: DragMode) => (event: React.PointerEvent<HTMLButtonElement | HTMLDivElement>) => {
    event.preventDefault();
    const pointerStartSec = secFromClientX(event.clientX);
    dragStateRef.current = {
      mode,
      pointerStartSec,
      startSec: draft.startSec,
      endSec: draft.endSec,
    };
    setDragMode(mode);
  };

  const commitDraft = () => {
    const startSec = clamp(Math.min(draft.startSec, draft.endSec - 0.1), 0, durationSec);
    const endSec = clamp(Math.max(draft.endSec, startSec + 0.1), startSec + 0.1, durationSec);

    onUpsertSegment({
      id: selectedSegment?.id ?? makeArcIntentId(),
      startSec,
      endSec,
      valence: clamp(draft.valence, -1, 1),
      intensity: clamp(draft.intensity, 0, 1),
      urgency: clamp(draft.urgency, 0, 1),
      transitionSteepness: clamp((draft.intensity + draft.urgency) / 2, 0, 1),
      authoredBy: "authored",
      focusScope: context.focusScope,
      focusClipId: context.focusClipId,
      focusSourceId: context.focusSourceId,
    });
  };

  const renderRibbonTab = () => (
    <div className="rounded-[22px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.03))] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-white/52">Emotional Ribbon</div>
          <div className="mt-1 text-[13px] font-semibold text-white/90">Drag the handles or the band to place the authored emotional window.</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex h-7 items-center rounded-full border border-white/10 bg-black/12 px-3 text-[10px] font-black uppercase tracking-[0.16em] text-white/72">Current {fmt(context.currentTimeSec)}</span>
          <span className="inline-flex h-7 items-center rounded-full border border-white/10 bg-black/12 px-3 text-[10px] font-black uppercase tracking-[0.16em] text-white/72">Duration {fmt(context.durationSec)}</span>
        </div>
      </div>

      <div className="mt-4 rounded-[20px] border border-white/10 bg-black/12 p-4">
        <div
          className="relative overflow-hidden rounded-[18px] border border-white/10 bg-slate-950/30 p-4"
          style={{ backgroundImage: `${ribbonBackground}, linear-gradient(180deg, rgba(2,6,23,0.18), rgba(15,23,42,0.32))` }}
        >
          <div className="pointer-events-none absolute inset-0 opacity-70" style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0))" }} />
          <div className="relative flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-[10px] font-black uppercase tracking-[0.18em] text-white/54">Active Vector</div>
              <div className="mt-1 text-[15px] font-semibold text-white/94">{draft.valence.toFixed(2)} · {draft.intensity.toFixed(2)} · {draft.urgency.toFixed(2)}</div>
            </div>
            <div className="flex flex-wrap gap-2">
              <SegmentChip label="MAG" value={vectorMagnitude(draft).toFixed(2)} />
              <SegmentChip label="START" value={fmt(draft.startSec)} />
              <SegmentChip label="END" value={fmt(draft.endSec)} />
            </div>
          </div>

          <div ref={trackRef} className="relative mt-4 h-20 rounded-[18px] border border-white/10 bg-slate-950/34 px-3 py-3">
            {sortedSegments.map((segment) => (
              <div
                key={segment.id}
                className="pointer-events-none absolute top-3 h-4 rounded-full border border-white/10 bg-white/[0.08]"
                style={{
                  left: `${pct(segment.startSec, durationSec)}%`,
                  width: `${Math.max(2, pct(segment.endSec, durationSec) - pct(segment.startSec, durationSec))}%`,
                }}
              />
            ))}

            <div className="pointer-events-none absolute inset-x-3 top-1/2 h-px -translate-y-1/2 bg-white/12" />

            <div
              role="presentation"
              onPointerDown={beginDrag("range")}
              className="absolute top-6 h-8 cursor-grab rounded-full border border-white/20 shadow-[0_10px_24px_rgba(2,6,23,0.24)] transition duration-200 hover:scale-[1.01] active:cursor-grabbing"
              style={{
                left: `${pct(draft.startSec, durationSec)}%`,
                width: `${Math.max(3, pct(draft.endSec, durationSec) - pct(draft.startSec, durationSec))}%`,
                backgroundImage: ribbonBackground,
              }}
            >
              <div className="absolute inset-0 rounded-full bg-white/[0.08]" />
            </div>

            <button
              type="button"
              aria-label="Drag arc start"
              onPointerDown={beginDrag("start")}
              className="absolute top-5 h-10 w-4 -translate-x-1/2 rounded-full border border-white/20 bg-white/[0.14] shadow-[0_10px_20px_rgba(2,6,23,0.24)] transition duration-200 hover:scale-105"
              style={{ left: `${pct(draft.startSec, durationSec)}%` }}
            />
            <button
              type="button"
              aria-label="Drag arc end"
              onPointerDown={beginDrag("end")}
              className="absolute top-5 h-10 w-4 -translate-x-1/2 rounded-full border border-white/20 bg-white/[0.14] shadow-[0_10px_20px_rgba(2,6,23,0.24)] transition duration-200 hover:scale-105"
              style={{ left: `${pct(draft.endSec, durationSec)}%` }}
            />

            <div className="absolute inset-x-3 bottom-2 flex items-center justify-between text-[10px] font-black uppercase tracking-[0.16em] text-white/48">
              <span>0:00</span>
              <span>{fmt(durationSec)}</span>
            </div>
          </div>

          <div className="mt-3 grid gap-2 md:grid-cols-3">
            <VectorCard label="Valence" value={fmtSigned(draft.valence)} hint="Emotional color. Positive lifts, negative weights." tone={vectorToneClasses("valence", draft.valence)} />
            <VectorCard label="Intensity" value={draft.intensity.toFixed(2)} hint="How much force the beat should carry across the range." tone={vectorToneClasses("intensity", draft.intensity)} />
            <VectorCard label="Urgency" value={draft.urgency.toFixed(2)} hint="Forward pressure and pulse toward the next cut or beat." tone={vectorToneClasses("urgency", draft.urgency)} />
          </div>
        </div>
      </div>
    </div>
  );

  const renderRecipesTab = () => (
    <div className="space-y-3 rounded-[22px] border border-white/10 bg-black/10 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-white/56">Recipes</div>
          <div className="mt-1 text-[12px] text-white/68">Selection-first recipes adjust only the active draft and stay non-canonical until saved.</div>
        </div>
        <span className="inline-flex h-7 items-center rounded-full border border-white/10 bg-white/[0.05] px-3 text-[10px] font-black uppercase tracking-[0.16em] text-white/70">{selectedSegment ? "Editing authored segment" : "Draft only"}</span>
      </div>

      <div className="grid gap-2 md:grid-cols-3">
        {recipes.map((recipe) => (
          <button
            key={recipe.label}
            type="button"
            onClick={() => {
              setDraft((prev) => ({
                ...prev,
                valence: recipe.valence,
                intensity: recipe.intensity,
                urgency: recipe.urgency,
              }));
            }}
            className="rounded-[18px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.03))] p-3 text-left transition duration-200 hover:-translate-y-[1px] hover:border-white/18 hover:bg-white/[0.07]"
          >
            <div className="flex items-start justify-between gap-2">
              <span className={["inline-flex h-6 items-center rounded-full border px-2.5 text-[10px] font-black uppercase tracking-[0.16em]", recipeToneClasses(recipe.tone)].join(" ")}>{recipe.label}</span>
              <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/44">Recipe</span>
            </div>
            <div className="mt-2 text-[12px] leading-5 text-white/72">{recipe.summary}</div>
            <div className="mt-3 flex flex-wrap gap-2">
              <SegmentChip label="V" value={fmtSigned(recipe.valence)} />
              <SegmentChip label="I" value={recipe.intensity.toFixed(2)} />
              <SegmentChip label="U" value={recipe.urgency.toFixed(2)} />
            </div>
          </button>
        ))}
      </div>

      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="space-y-3 rounded-[20px] border border-white/10 bg-white/[0.04] p-3">
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-white/56">Range Anchors</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="space-y-1.5">
              <span className="text-[10px] font-black uppercase tracking-[0.16em] text-white/56">Start</span>
              <input
                type="number"
                min={0}
                max={durationSec}
                step={0.1}
                value={draft.startSec}
                onChange={(event) => {
                  const startSec = clamp(Number(event.target.value || 0), 0, durationSec);
                  setDraft((prev) => ({
                    ...prev,
                    startSec,
                    endSec: Math.max(startSec + 0.1, prev.endSec),
                  }));
                }}
                className="w-full rounded-[14px] border border-white/12 bg-slate-950/24 px-3 py-2 text-sm text-white outline-none"
              />
            </label>
            <label className="space-y-1.5">
              <span className="text-[10px] font-black uppercase tracking-[0.16em] text-white/56">End</span>
              <input
                type="number"
                min={0}
                max={durationSec}
                step={0.1}
                value={draft.endSec}
                onChange={(event) => {
                  const endSec = clamp(Number(event.target.value || 0), 0, durationSec);
                  setDraft((prev) => ({
                    ...prev,
                    endSec: Math.max(prev.startSec + 0.1, endSec),
                  }));
                }}
                className="w-full rounded-[14px] border border-white/12 bg-slate-950/24 px-3 py-2 text-sm text-white outline-none"
              />
            </label>
          </div>
        </div>

        <div className="space-y-3 rounded-[20px] border border-white/10 bg-white/[0.04] p-3">
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-white/56">Emotional Vector</div>
          <ControlSlider label="Valence" value={draft.valence} min={-1} max={1} step={0.05} onChange={(value) => setDraft((prev) => ({ ...prev, valence: value }))} accent="accent-emerald-300" />
          <ControlSlider label="Intensity" value={draft.intensity} min={0} max={1} step={0.05} onChange={(value) => setDraft((prev) => ({ ...prev, intensity: value }))} accent="accent-cyan-300" />
          <ControlSlider label="Urgency" value={draft.urgency} min={0} max={1} step={0.05} onChange={(value) => setDraft((prev) => ({ ...prev, urgency: value }))} accent="accent-amber-300" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <button
          type="button"
          onClick={commitDraft}
          className="inline-flex h-9 items-center rounded-full border border-emerald-300/24 bg-emerald-400/14 px-4 text-[10px] font-black tracking-[0.18em] text-emerald-50 transition hover:bg-emerald-400/18"
        >
          {selectedSegment ? "UPDATE ARC" : "ADD ARC"}
        </button>
        <button
          type="button"
          onClick={() => {
            setSelectedSegmentId(null);
            setDraft(buildDefaultDraft(context));
          }}
          className="inline-flex h-9 items-center rounded-full border border-white/12 bg-white/[0.06] px-4 text-[10px] font-black tracking-[0.18em] text-white/78 transition hover:bg-white/[0.09]"
        >
          RESET DRAFT
        </button>
        {selectedSegment ? (
          <button
            type="button"
            onClick={() => {
              onRemoveSegment(selectedSegment.id);
              setSelectedSegmentId(null);
              setActiveTab("segments");
            }}
            className="inline-flex h-9 items-center rounded-full border border-rose-300/24 bg-rose-400/12 px-4 text-[10px] font-black tracking-[0.18em] text-rose-50 transition hover:bg-rose-400/16"
          >
            REMOVE SEGMENT
          </button>
        ) : null}
      </div>
    </div>
  );

  const renderSegmentsTab = () => (
    <div className="space-y-3 rounded-[22px] border border-white/10 bg-black/10 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-white/56">Authored Segments</div>
          <div className="mt-1 text-[12px] text-white/68">Structured ARC objects tied to the current selection context.</div>
        </div>
        <span className="inline-flex h-7 items-center rounded-full border border-white/10 bg-white/[0.05] px-3 text-[10px] font-black uppercase tracking-[0.16em] text-white/70">{sortedSegments.length} total</span>
      </div>

      {sortedSegments.length === 0 ? (
        <div className="rounded-[18px] border border-dashed border-white/10 bg-white/[0.03] px-3 py-4 text-[12px] leading-5 text-white/60">
          No ARC intent authored yet. Drag a range, tune the vector, and save the first authored emotional layer.
        </div>
      ) : (
        <div className="grid gap-2 md:grid-cols-2">
          {sortedSegments.map((segment) => {
            const active = segment.id === selectedSegmentId;
            return (
              <button
                key={segment.id}
                type="button"
                onClick={() => {
                  setSelectedSegmentId(segment.id);
                  setActiveTab("recipes");
                }}
                className="w-full rounded-[18px] border px-3 py-3 text-left transition duration-200 hover:-translate-y-[1px]"
                style={{
                  borderColor: active ? "rgba(52,211,153,0.34)" : "rgba(255,255,255,0.10)",
                  background: active
                    ? "linear-gradient(180deg, rgba(16,185,129,0.16), rgba(6,78,59,0.28))"
                    : "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.03))",
                }}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-[12px] font-semibold text-white/94">{fmt(segment.startSec)} to {fmt(segment.endSec)}</div>
                    <div className="mt-1 text-[11px] text-white/60">{segment.focusClipId || segment.focusSourceId || segment.focusScope}</div>
                  </div>
                  <span className="text-[10px] font-black uppercase tracking-[0.16em] text-white/48">{segment.focusScope}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <SegmentChip label="VAL" value={segment.valence.toFixed(2)} />
                  <SegmentChip label="INT" value={segment.intensity.toFixed(2)} />
                  <SegmentChip label="URG" value={segment.urgency.toFixed(2)} />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  if (compact) {
    return (
      <div className="rounded-[22px] border border-emerald-300/18 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(2,6,23,0.10))] p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex h-6 items-center rounded-full border border-emerald-300/22 bg-emerald-400/14 px-2.5 text-[10px] font-black uppercase tracking-[0.18em] text-emerald-50">Arc Layer</span>
          <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/54">{sortedSegments.length} segment{sortedSegments.length === 1 ? "" : "s"}</span>
        </div>
        <div className="mt-3 rounded-[16px] border border-white/10 bg-black/12 p-3">
          <div className="text-[12px] font-semibold text-white/92">{selectionLabel}</div>
          <div className="mt-2 text-[11px] text-white/66">Vector {vectorLabel}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? "space-y-1.5 rounded-[22px] border border-emerald-300/16 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(2,6,23,0.08))] p-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]" : "space-y-3 rounded-[24px] border border-emerald-300/16 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(2,6,23,0.10)),radial-gradient(circle_at_top_left,rgba(52,211,153,0.08),rgba(52,211,153,0)_28%),radial-gradient(circle_at_78%_18%,rgba(96,165,250,0.08),rgba(96,165,250,0)_24%)] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]"}>
      {!embedded ? (
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex h-7 items-center rounded-full border border-emerald-300/22 bg-emerald-400/14 px-3 text-[10px] font-black uppercase tracking-[0.18em] text-emerald-50">Arc Layer</span>
              <span className="inline-flex h-7 items-center rounded-full border border-white/10 bg-white/[0.05] px-3 text-[10px] font-black uppercase tracking-[0.18em] text-white/72">Selection First</span>
            </div>
            <div className="mt-2 text-[15px] font-semibold text-white/94">Emotional Vector authoring for the active focus.</div>
            <div className="mt-1 text-[12px] leading-5 text-white/68">ARC remains authored metadata. It follows the current selection anchor instead of mutating clips, markers, or timing truth.</div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex h-8 items-center rounded-full border border-white/10 bg-white/[0.05] px-3 text-[10px] font-black uppercase tracking-[0.16em] text-white/72">Focus {selectionLabel}</span>
            <span className="inline-flex h-8 items-center rounded-full border border-cyan-300/22 bg-cyan-400/12 px-3 text-[10px] font-black uppercase tracking-[0.16em] text-cyan-50">Vector {vectorLabel}</span>
            <span className="inline-flex h-8 items-center rounded-full border border-emerald-300/22 bg-emerald-400/12 px-3 text-[10px] font-black uppercase tracking-[0.16em] text-emerald-50">Range {currentRangeLabel}</span>
          </div>
        </div>
      ) : null}

      {embedded ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-[18px] border border-white/10 bg-white/[0.04] px-2 py-2">
          <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
            <LayerTabButton active={activeTab === "ribbon"} label="Ribbon" summary="Range placement and live emotional shape." onClick={() => setActiveTab("ribbon")} compact />
            <LayerTabButton active={activeTab === "recipes"} label="Draft" summary="Recipes, vector controls, and save actions." onClick={() => setActiveTab("recipes")} compact />
            <LayerTabButton active={activeTab === "segments"} label="Segments" summary={`Authored layer review. ${segmentCountLabel}.`} onClick={() => setActiveTab("segments")} compact />
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="inline-flex h-6 items-center rounded-full border border-white/10 bg-white/[0.05] px-2.5 text-[9px] font-black uppercase tracking-[0.16em] text-white/68">{currentRangeLabel}</span>
            <span className="inline-flex h-6 items-center rounded-full border border-cyan-300/22 bg-cyan-400/12 px-2.5 text-[9px] font-black uppercase tracking-[0.16em] text-cyan-50">{segmentCountLabel}</span>
          </div>
        </div>
      ) : (
        <div className="grid gap-2 md:grid-cols-3">
          <LayerTabButton active={activeTab === "ribbon"} label="Ribbon" summary="Range placement and live emotional shape." onClick={() => setActiveTab("ribbon")} />
          <LayerTabButton active={activeTab === "recipes"} label="Draft" summary="Recipes, vector controls, and save actions." onClick={() => setActiveTab("recipes")} />
          <LayerTabButton active={activeTab === "segments"} label="Segments" summary={`Authored layer review. ${segmentCountLabel}.`} onClick={() => setActiveTab("segments")} />
        </div>
      )}

      {activeTab === "ribbon" ? renderRibbonTab() : null}
      {activeTab === "recipes" ? renderRecipesTab() : null}
      {activeTab === "segments" ? renderSegmentsTab() : null}
    </div>
  );
}
